/**
 * DeleteUserModal Component
 *
 * Confirmation modal for deleting/removing one or more users from the system.
 * Supports both single user deletion and bulk deletion.
 *
 * @see Figma design: node-id=1884-87369
 */
import React, { useState } from 'react';
import PropTypes from 'prop-types';
import styled from 'styled-components';
import LytModal from '../ui-core/modal/LytModal';
import LytButton from '../library/button/LytButton';
import LytIcon from '../library/icon/LytIcon';
import { ICON_NAMES } from '../library/icon/icon-constants';
import {
    applyTypography,
    TYPOGRAPHY_VARIANTS,
} from '../styled/theme/typography-utils';

// ============================================================================
// STYLED COMPONENTS
// ============================================================================

const ModalContent = styled.div`
    display: flex;
    flex-direction: column;
`;
const BodyText = styled.p`
    ${applyTypography(TYPOGRAPHY_VARIANTS.SIZE_1_NORMAL)}
    line-height: 18px;
    color: ${({ theme }) => theme?.text?.default};
    margin: 0;
    padding: 4px 0 8px 0;
`;
const UserName = styled.span`
    ${applyTypography(TYPOGRAPHY_VARIANTS.SIZE_1_BOLD)}
    line-height: 18px;
    color: ${({ theme }) => theme?.text?.default};
`;
const UserList = styled.ul`
    margin: 8px 0 0 0;
    padding: 0 0 0 20px;
    max-height: 200px;
    overflow-y: auto;
`;
const UserListItem = styled.li`
    ${applyTypography(TYPOGRAPHY_VARIANTS.SIZE_1_NORMAL)}
    line-height: 24px;
    color: ${({ theme }) => theme?.text?.default};
`;
const ButtonGroup = styled.div`
    display: flex;
    align-items: center;
    gap: 4px;
`;

// ============================================================================
// HELPERS
// ============================================================================

/**
 * Get display name for a user object
 */
const getUserDisplayName = (user) => {
    const firstName = user?.firstname || user?.first_name || '';
    const lastName = user?.lastname || user?.last_name || '';
    const fullName = `${firstName} ${lastName}`.trim();
    return fullName || user?.email || 'Unknown user';
};

/**
 * Normalize users input to always be an array
 */
const normalizeUsers = (user, users) => {
    if (users && Array.isArray(users) && users.length > 0) {
        return users;
    }
    if (user && typeof user === 'object' && Object.keys(user).length > 0) {
        return [user];
    }
    return [];
};

// ============================================================================
// COMPONENT
// ============================================================================

/**
 * DeleteUserModal - Confirmation dialog for removing one or more users
 *
 * @param {Object} props - Component props
 * @param {boolean} props.open - Whether the modal is open
 * @param {Function} props.onClose - Callback when modal is closed (cancel)
 * @param {Function} props.closeModal - Function to close modal with result (from ModalProvider)
 * @param {Function} props.onConfirm - Optional callback when user confirms deletion
 * @param {Object} props.user - Single user object being deleted (for backwards compatibility)
 * @param {Array} props.users - Array of user objects being deleted (for bulk delete)
 */
const DeleteUserModal = ({
    open = false,
    onClose = undefined,
    closeModal = undefined,
    onConfirm = undefined,
    user = {},
    users = [],
}) => {
    const [loading, setLoading] = useState(false);
    if (!open) return null;

    // Normalize to always work with an array
    const userList = normalizeUsers(user, users);
    const isBulkDelete = userList.length > 1;
    const userCount = userList.length;

    // Handle cancel button click
    const handleCancel = () => {
        closeModal?.({
            confirmed: false,
        });
    };

    // Handle confirm button click
    const handleConfirm = async () => {
        setLoading(true);
        try {
            // Call the onConfirm callback if provided
            if (onConfirm) {
                await onConfirm(isBulkDelete ? userList : userList[0]);
            }
            // Close the modal with confirmed result
            closeModal?.({
                confirmed: true,
                user: isBulkDelete ? undefined : userList[0],
                users: isBulkDelete ? userList : undefined,
            });
        } catch (error) {
            console.error('Error during user deletion:', error);
            setLoading(false);
        }
    };

    // Modal footer actions
    const footerActions = (
        <ButtonGroup>
            <LytButton
                type="secondary"
                onClick={handleCancel}
                disabled={loading}>
                Cancel
            </LytButton>
            <LytButton
                type="danger"
                onClick={handleConfirm}
                loading={loading}
                disabled={loading}>
                {isBulkDelete
                    ? `Remove ${userCount} Accounts`
                    : 'Remove Account'}
            </LytButton>
        </ButtonGroup>
    );

    // Render content based on single vs bulk delete
    const renderContent = () => {
        if (isBulkDelete) {
            return (
                <ModalContent>
                    <BodyText>
                        Are you sure you want to remove{' '}
                        <UserName>{userCount} users</UserName>? They will no
                        longer have access to this account. You can re-invite
                        them later if needed.
                    </BodyText>
                    <UserList>
                        {userList.map((u, index) => (
                            <UserListItem key={u?.id || u?.email || index}>
                                {getUserDisplayName(u)}
                            </UserListItem>
                        ))}
                    </UserList>
                </ModalContent>
            );
        }
        return (
            <ModalContent>
                <BodyText>
                    Are you sure you want to remove user?{' '}
                    <UserName>{getUserDisplayName(userList[0])}</UserName> will
                    no longer have access to this account. You can re-invite
                    them later if needed.
                </BodyText>
            </ModalContent>
        );
    };
    return (
        <LytModal
            open={open}
            title="Are you sure?"
            titleIcon={
                <LytIcon
                    name={ICON_NAMES.BrightnessAlertFill}
                    size={24}
                    color="#C8322A"
                />
            }
            onClose={handleCancel}
            actions={footerActions}
            size="small">
            {renderContent()}
        </LytModal>
    );
};
DeleteUserModal.propTypes = {
    open: PropTypes.bool,
    onClose: PropTypes.func,
    closeModal: PropTypes.func,
    onConfirm: PropTypes.func,
    user: PropTypes.shape({
        id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
        email: PropTypes.string,
        firstname: PropTypes.string,
        lastname: PropTypes.string,
        first_name: PropTypes.string,
        last_name: PropTypes.string,
    }),
    users: PropTypes.arrayOf(
        PropTypes.shape({
            id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
            email: PropTypes.string,
            firstname: PropTypes.string,
            lastname: PropTypes.string,
            first_name: PropTypes.string,
            last_name: PropTypes.string,
        })
    ),
};
export default DeleteUserModal;
